import { useEffect, useRef, useState } from "react";
import { X, Volume2, FolderPlus } from "lucide-react";
import { SpecimenIcon } from "../../lib/icons";
import { playVoice } from "../../lib/voice";
import { playMemory, splitSentences } from "../../lib/pensieve";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_TEXT = {
  decoded: "Memory recovered // new entry for the case file",
  known: "Memory replayed // already in the case file",
  review: "Case file entry // replay",
};

const useSubtitles = (script, elapsed, duration) => {
  const sentences = splitSentences(script);
  const words = sentences.map((s) => s.split(/\s+/).length);
  const totalWords = words.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  let idx = 0;
  for (let i = 0; i < sentences.length; i++) {
    const start = (acc / totalWords) * (duration || 1);
    if (elapsed >= start) idx = i;
    acc += words[i];
  }
  return { sentences, idx };
};

export const FlashReveal = ({ reveal, onClose }) => {
  const [state, setState] = useState("loading");
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const ctrl = useRef(null);
  const closeTimer = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!reveal) return undefined;
    const { specimen } = reveal;
    let cancelled = false;
    setState("loading");
    setElapsed(0);
    setDuration(0);
    playVoice(specimen.voice, specimen.sound_word, specimen.spore_word);
    playMemory({
      voiceUrl: `${API}/api/memory/${specimen.id}/voice`,
      sfxUrl: `${API}/api/memory/${specimen.id}/sfx`,
      onProgress: (el, dur) => { if (!cancelled) { setElapsed(el); setDuration(dur); } },
      onEnd: () => { if (!cancelled) { setState("done"); closeTimer.current = setTimeout(() => onCloseRef.current(), 7000); } },
    }).then((c) => {
      if (cancelled) { c.stop(); return; }
      ctrl.current = c;
      setState(c.hasVoice ? "playing" : "silent");
      if (!c.hasVoice) closeTimer.current = setTimeout(() => onCloseRef.current(), 16000);
    });
    return () => { cancelled = true; clearTimeout(closeTimer.current); ctrl.current?.stop(); ctrl.current = null; };
  }, [reveal]);

  const { sentences, idx } = useSubtitles(reveal?.specimen.memory?.script || "", elapsed, duration);
  if (!reveal) return null;
  const { specimen, status } = reveal;
  const m = specimen.memory || {};
  const showAll = state === "silent" || state === "done";
  const pct = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

  return (
    <div data-testid="flash-reveal-modal" data-state={state} className="fixed inset-0 z-40 flex items-center justify-center bg-[#050807]/90 p-6 backdrop-blur-xl" onClick={onClose}>
      <div className="pensieve-swirl" />
      <div className="pensieve-ripple" /><div className="pensieve-ripple" style={{ animationDelay: "1.3s" }} /><div className="pensieve-ripple" style={{ animationDelay: "2.6s" }} />
      <div key={`${specimen.id}-${reveal.at}`} className="flash-card relative w-full max-w-6xl border border-emerald-400/40 bg-[#070c0a]/85 p-8 sm:p-12" onClick={(e) => e.stopPropagation()}>
        <span className="corner corner-tl" /><span className="corner corner-tr" /><span className="corner corner-bl" /><span className="corner corner-br" />
        <button data-testid="flash-close-button" onClick={onClose} className="absolute right-4 top-4 text-slate-500 hover:text-slate-200 transition-colors"><X size={22} /></button>
        <p data-testid="flash-status" className={`font-mono text-xs sm:text-sm uppercase tracking-[0.35em] ${status === "decoded" ? "text-emerald-300" : "text-amber-300"}`}>
          Specimen #{String(specimen.slot).padStart(2, "0")} // {STATUS_TEXT[status]}
        </p>
        <div className="mt-8 grid items-start gap-10 md:grid-cols-[220px_1fr]">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flash-icon-frame flex h-40 w-40 items-center justify-center text-emerald-300">
              <SpecimenIcon name={specimen.icon} size={88} strokeWidth={1.4} />
            </div>
            <p data-testid="flash-object-name" className="font-display text-xl font-bold uppercase tracking-[0.12em] text-slate-200">{specimen.name}</p>
            <button data-testid="flash-replay-button" onClick={(e) => { e.stopPropagation(); playVoice(specimen.voice, specimen.sound_word, specimen.spore_word); }} className="flex items-center gap-2 border border-emerald-400/30 px-3 py-1.5 font-mono text-xs text-emerald-300 hover:bg-emerald-400/10 transition-colors" title="The hive's word">
              <Volume2 size={14} /> <span data-testid="flash-spore-word">{specimen.spore_word}</span>
            </button>
            <p data-testid="flash-spore-meaning" className="font-mono text-[11px] uppercase tracking-widest text-slate-500">translation: "{specimen.meaning}"</p>
          </div>
          <div>
            <p className="lab-label">Memory · age {m.age}{m.year ? ` · ${m.year}` : ""}</p>
            <h2 data-testid="flash-memory-title" className="spore-headline mt-2 text-4xl sm:text-5xl lg:text-6xl">{m.title}</h2>
            <div className="mt-7 min-h-[150px] border-l-2 border-amber-300/70 pl-5">
              <p className="scientist-label">{state === "loading" ? "Retrieving memory from the hive..." : "His voice"}</p>
              <div data-testid="flash-script" className="mt-3 space-y-2">
                {sentences.map((s, i) => (
                  <p key={i} data-testid={i === idx ? "flash-subtitle-current" : undefined} className={`scientist-line transition-all duration-500 ${showAll || i === idx ? "text-xl sm:text-2xl text-slate-50" : i < idx ? "text-base text-slate-500" : "text-base text-slate-700"}`}>
                    {s}
                  </p>
                ))}
              </div>
            </div>
            <div className="mt-6 h-[2px] w-full overflow-hidden bg-slate-800">
              <div className="h-full bg-amber-300 transition-[width] duration-200" style={{ width: `${showAll ? 100 : pct}%` }} />
            </div>
            <div data-testid="flash-clue" className={`mt-6 flex items-start gap-3 border border-amber-300/40 bg-amber-300/5 p-4 transition-opacity duration-700 ${showAll ? "opacity-100" : "opacity-0"}`}>
              <FolderPlus size={18} className="mt-0.5 shrink-0 text-amber-300" />
              <div>
                <p className="scientist-label">{status === "decoded" ? "Clue added to case file" : "Case file clue"} · {m.category}</p>
                <p className="mt-1 text-base text-slate-100">{m.clue}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
